import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaClock, FaCalendarAlt, FaStar, FaCheckCircle, FaArrowRight,
  FaBookOpen, FaCertificate, FaTrophy, FaUsers, FaHospital, FaGlobe,
  FaChevronDown, FaChevronUp, FaRupeeSign, FaAward, FaListUl
} from 'react-icons/fa';

const PageContainer = styled.div`
  margin-top: 180px;
  background: #ffffff;

  @media (max-width: 1366px) and (min-width: 769px) {
    margin-top: 155px;
  }


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

/* ========== HERO ========== */
const HeroSection = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #3730A3 50%, #0b0b77 100%);
  color: white;
  padding: 2.5rem 5%;
  text-align: center;
`;

const HeroBadge = styled.span`
  display: inline-flex; align-items: center; gap: 0.5rem;
  background: rgba(255,255,255,0.1); padding: 0.4rem 1.2rem; border-radius: 50px;
  font-size: 0.9rem; font-weight: 600; margin-bottom: 1rem;
  svg { color: #a78bfa; }
`;

const HeroTitle = styled.h1`
font-family: 'Times New Roman', serif;
  font-size: 2.8rem; font-weight: 800; margin-bottom: 0.5rem;
  span { display: block; color: #a78bfa; font-size: 1.2rem; font-weight: 400; margin-top: 0.2rem; }
  @media (max-width: 768px) { font-size: 2rem; span { font-size: 1rem; } }
`;

const HeroDesc = styled.p`
font-family: 'Times New Roman', serif;
  font-size: 1rem; opacity: 0.85; max-width: 650px; margin: 0.5rem auto 1rem;
`;

const HeroInfo = styled.div`
  display: flex; justify-content: center; gap: 1.5rem; flex-wrap: wrap; margin-top: 1rem;
  div { display: flex; align-items: center; gap: 0.4rem; background: rgba(255,255,255,0.08); padding: 0.5rem 1rem; border-radius: 30px; font-size: 0.9rem; 
    svg { color: #a78bfa; } strong { color: #a78bfa; } }
`;

/* ========== CONTAINER ========== */
const Container = styled.div`
  max-width: 1100px; margin: 0 auto; padding: 2.5rem 5% 3rem;
`;

/* ========== LAYOUT ========== */
const TwoCol = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; margin-bottom: 1.5rem;
  @media (max-width: 800px) { grid-template-columns: 1fr; }
`;

/* ========== CARDS ========== */
const SectionCard = styled.div`
  background: white; border-radius: 14px; padding: 1.5rem;
  border: 1px solid #f0f0f5; box-shadow: 0 2px 10px rgba(0,0,0,0.02);
`;

const CardTitle = styled.h3`
font-family: 'Times New Roman', serif;
  font-size: 1.1rem; color: #1a1a2e; font-weight: 700; margin-bottom: 1rem;
  display: flex; align-items: center; gap: 0.6rem;
  .card-icon { width: 36px; height: 36px; border-radius: 10px; background: #f5f3ff; color: #3730A3; display: flex; align-items: center; justify-content: center; font-size: 1rem; }
`;

const SimpleList = styled.ul`
font-family: 'Times New Roman', serif;
  list-style: none; padding: 0;
  li { display: flex; align-items: flex-start; gap: 0.6rem; padding: 0.4rem 0; color: #4a4a6a; font-size: 0.9rem; line-height: 1.5;
    svg { color: #3730A3; font-size: 0.85rem; margin-top: 3px; flex-shrink: 0; } }
`;

const NumberedList = styled.div`
  display: flex; flex-direction: column; gap: 0.5rem;
`;

const NumberedItem = styled.div`
font-family: 'Times New Roman', serif;
  display: flex; align-items: flex-start; gap: 0.8rem;
  .num { width: 28px; height: 28px; border-radius: 50%; background: #3730A3; color: white; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.8rem; flex-shrink: 0; }
  p { color: #4a4a6a; font-size: 0.9rem; line-height: 1.5; }
`;

/* ========== CURRICULUM COLLAPSIBLE ========== */
const CurrCard = styled.div`
  background: white; border-radius: 12px; margin-bottom: 0.6rem;
  border: 1px solid #f0f0f5; overflow: hidden;
`;

const CurrHeader = styled.div`
font-family: 'Times New Roman', serif;
  display: flex; align-items: center; justify-content: space-between;
  padding: 1rem 1.2rem; background: ${props => props.open ? '#f5f3ff' : '#fafbff'}; cursor: pointer;
  transition: all 0.3s ease;
  &:hover { background: #f5f3ff; }
  .curr-left { display: flex; align-items: center; gap: 0.8rem; font-weight: 700; color: #1a1a2e; font-size: 0.95rem;
    svg { color: #3730A3; } }
  .curr-arrow { color: #3730A3; transition: transform 0.3s ease; }
`;

const CurrBody = styled.div`
font-family: 'Times New Roman', serif;
  padding: ${props => props.open ? '1rem 1.2rem' : '0 1.2rem'};
  max-height: ${props => props.open ? '800px' : '0'}; overflow: hidden; transition: all 0.3s ease;
  ul { list-style: none; padding: 0;
    li { display: flex; align-items: center; gap: 0.5rem; padding: 0.4rem 0; color: #4a4a6a; font-size: 0.85rem; border-bottom: 1px solid #f5f5f8;
      &:last-child { border-bottom: none; }
      &::before { content: '▸'; color: #3730A3; font-weight: bold; } } }
`;

/* ========== CERT BLOCK ========== */
const CertBlock = styled.div`
  background: #fff;
  border: 1px solid #f0f0f5;
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 1.5rem;
`;

const CertHead = styled.div`
  background: #3730A3;
  padding: 1.2rem 1.8rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.8rem;
`;

const CertHeadTitle = styled.div`
  font-family: 'Times New Roman', serif;
  font-size: 1.1rem;
  color: #fff;
  font-weight: 800;
`;

const CertHeadBadge = styled.div`
  font-family: 'Times New Roman', serif;
  background: rgba(167, 139, 250, 0.2);
  color: #a78bfa;
  border: 1px solid rgba(167, 139, 250, 0.3);
  font-size: 0.72rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 0.3rem 0.8rem;
  border-radius: 4px;
  font-weight: 700;
`;

const CertBody = styled.div`
  padding: 1.8rem;
`;

const CertDesc = styled.p`
  font-family: 'Times New Roman', serif;
  font-size: 0.95rem;
  color: #4a4a6a;
  line-height: 1.8;
  margin-bottom: 1.8rem;
  border-left: 3px solid #a78bfa;
  padding-left: 1.2rem;
`;

const CertCols = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.8rem;

  @media (max-width: 650px) { grid-template-columns: 1fr; }
`;

const CertColTitle = styled.div`
  font-family: 'Times New Roman', serif;
  font-size: 0.78rem;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: #3730A3;
  font-weight: 700;
  margin-bottom: 0.8rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PlainList = styled.ul`
  list-style: none;
  padding: 0;

  li {
    font-family: 'Times New Roman', serif;
    font-size: 0.92rem;
    color: #4a4a6a;
    padding: 0.35rem 0;
    border-bottom: 1px solid #f0f0f5;
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    line-height: 1.5;

    &:last-child { border-bottom: none; }

    svg { color: #3730A3; font-size: 0.8rem; margin-top: 3px; flex-shrink: 0; }
  }
`;

const FeeTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: 'Times New Roman', serif;
  margin-bottom: 0;

  th {
    background: #1a1a2e;
    color: rgba(255,255,255,0.7);
    font-size: 0.75rem;
    letter-spacing: 1px;
    text-transform: uppercase;
    padding: 0.7rem 1.2rem;
    text-align: left;
    font-weight: 700;
  }

  td {
    padding: 0.9rem 1.2rem;
    border-bottom: 1px solid #f0f0f5;
    font-size: 0.95rem;
    color: #4a4a6a;
  }

  tr:last-child td { border-bottom: none; }

  .amt {
    font-size: 1.5rem;
    font-weight: 900;
    color: #1a1a2e;
  }

  tr.highlight { background: #f5f3ff; }
  tr.highlight .amt { color: #3730A3; }
  tr.highlight td { border-bottom: none; }
`;

/* ========== CTA ========== */
const CTASection = styled.div`
  margin: 2rem 0 1rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  background: #1a1a2e;
  border-radius: 14px;
  overflow: hidden;
  padding: 1.5rem 1.5rem 1.5rem 0;

  &::before {
    content: '';
    width: 5px;
    align-self: stretch;
    background: #a78bfa;
    flex-shrink: 0;
  }

  @media (max-width: 650px) {
    flex-direction: column;
    padding: 1.5rem;
    &::before { display: none; }
  }
`;

const CTAText = styled.div`
  flex: 1;
  .cta-title {
    font-family: 'Times New Roman', serif;
    font-size: 1.4rem;
    font-weight: 800;
    color: #fff;
    margin-bottom: 0.3rem;
  }
  .cta-sub {
    font-family: 'Times New Roman', serif;
    font-size: 0.95rem;
    color: rgba(255,255,255,0.55);
    font-style: italic;
  }
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #a78bfa;
  color: #1a1a2e;
  font-family: 'Times New Roman', serif;
  font-weight: 700;
  font-size: 0.95rem;
  padding: 0.8rem 1.8rem;
  border-radius: 50px;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover { 
    background: #c4b5fd; 
    transform: translateY(-2px);
  }
`;

const Spacer = styled.div`
  height: 2rem;
`;

const InpatientDRGCoding = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [openModules, setOpenModules] = useState({});

  const toggleModule = (index) => {
    setOpenModules(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const topics = [
    "Overview of U.S. Healthcare",
    "Overview of Reimbursement Methodologies",
    "Medical Record Documentation",
    "U.S. Healthcare Legal & Regulatory Guidelines",
    "Compliance, Edits, & Reports",
    "ICD-10 CM Coding",
    "ICD-10 PCS Coding"
  ];

  const benefits = [
    "A long-term stable career with plenty of opportunities to work onsite in the U.S., Middle East, and other countries",
    "Comprehensive knowledge of hospital and physician practice coding",
    "Increases earning potential",
    "By gaining this certification, you can improve your potential for employability",
    "Getting certified boosts your confidence on the subject, which in turn would help your overall performance",
    "Track record of passing rate of more than 98% apart from Trainee Satisfaction",
    "Placement assistance provided for merit candidates"
  ];

  const whoShouldAttend = [
    "Medical, Paramedical and Life Science Graduates and Post Graduates (M.B.B.S., B.D.S., B.H.M.S., B.A.M.S., Pharmacy, Physiotherapy, Nursing, Occupational Therapy, Microbiology, Biochemistry, Biotechnology, Genetics, Biology, Bio-Medical graduates, Zoology, Botany, Bioinformatics, Endocrinology, Nutrition & Dietetics, Anatomy, Physiology, Health Education, etc.)",
    "Candidates already working in outpatient settings like ED Professional/Facility, E&M Coding etc., who are willing to advance their career in medical coding",
    "Candidates working in HCC Coding, who are willing to explore other options in their career",
    "Candidates who are willing to learn Inpatient DRG Coding",
    "Candidates who are willing to move to the Middle East for opportunities in medical coding",
    "Candidates who are already working in the Middle East and are willing to advance their coding career with Inpatient Coding",
    "Candidates who are willing to move to the U.S. for employment opportunities or for any other reason"
  ];

  const curriculum = [
    { title: "Overview of U.S. Healthcare", items: ["Revenue Cycle Management", "Different Types of Insurance Providers", "Different Types of Billing Forms"] },
    { title: "Overview of Reimbursement Methodologies", items: ["Inpatient Prospective Payment System (IPPS)", "Diagnostic Related Groups (DRGs)", "DRG Relative Weights", "Payment Adjustments", "Outlier Payments", "Transfer Policies"] },
    { title: "Medical Record Documentation", items: ["JCAHO Guidelines for Medical Record Documentation and Standards", "UHDDS Definitions and Practical Application"] },
    { title: "U.S. Healthcare Legal & Regulatory Guidelines", items: ["Affordable Care Act", "Health Insurance Portability and Accountability Act (HIPAA)", "Health Information Technology For Economic and Clinical Health Act (HITECH)"] },
    { title: "Compliance, Edits & Reports", items: ["Compliance Policies for Hospital and Physician Practices", "Corporate Integrity Agreements", "National Correct Coding Initiative (NCCI)", "Medicare Code Edits (MCE)", "Medically Unlikely Edits (MUE)", "FATHOM, PEPPER, CERT, CBR Reports", "Coding Clinic Updates"] },
    { title: "ICD-10 CM Coding", items: ["ICD-10 CM Coding for Inpatient and Outpatient Diagnostic Reporting", "Surgical Package & Global Period Modifiers", "Practical Coding Scenarios"] }
  ];

  const inclusions = [
    '50 Hours Expert Training',
    'Study Materials & Practice Exercises',
    'Mock Exams (CGSC Pattern)',
    'One-on-One Mentoring',
    'Placement Assistance',
    'Certificate of Completion'
  ];

  const whatYoullLearn = [
    'ICD-10-CM Coding for Inpatient Settings',
    'ICD-10-PCS Coding Structure & Guidelines',
    'DRG Assignment & Reimbursement Methodology',
    'Inpatient Prospective Payment System (IPPS)',
    'Medical Record Documentation Standards',
    'Compliance & Regulatory Guidelines'
  ];

  return (
    <PageContainer>
      {/* HERO */}
      <HeroSection>
        <HeroBadge><FaHospital /> Specialty Program</HeroBadge>
        <HeroTitle>Inpatient DRG Coding Training<span>Master Hospital Reimbursement Coding</span></HeroTitle>
        <HeroDesc>
          Comprehensive training in Diagnosis Related Groups, ICD-10-CM/PCS coding, and hospital reimbursement methodologies.
        </HeroDesc>
        <HeroInfo>
          <div><FaClock /> Duration: <strong>50 Hours</strong></div>
          <div><FaCalendarAlt /> <strong>8-10 Weeks</strong></div>
          <div><FaTrophy /> Pass Rate: <strong>98%</strong></div>
          <div><FaGlobe /> <strong>Global Opportunities</strong></div>
        </HeroInfo>
      </HeroSection>

      <Container>
        {/* ROW 1: Topics + Benefits */}
        <TwoCol>
          <SectionCard>
            <CardTitle><span className="card-icon"><FaBookOpen /></span>Topics Covered</CardTitle>
            <SimpleList>
              {topics.map((t, i) => <li key={i}><FaCheckCircle /> {t}</li>)}
            </SimpleList>
          </SectionCard>

          <SectionCard>
            <CardTitle><span className="card-icon"><FaTrophy /></span>Program Benefits</CardTitle>
            <SimpleList>
              {benefits.map((b, i) => <li key={i}><FaCheckCircle /> {b}</li>)}
            </SimpleList>
          </SectionCard>
        </TwoCol>

        {/* WHO SHOULD ATTEND */}
        <SectionCard style={{marginBottom:'1.5rem'}}>
          <CardTitle><span className="card-icon"><FaUsers /></span>Who Should Attend?</CardTitle>
          <NumberedList>
            {whoShouldAttend.map((item, i) => (
              <NumberedItem key={i}><span className="num">{i+1}</span><p>{item}</p></NumberedItem>
            ))}
          </NumberedList>
        </SectionCard>

        {/* CURRICULUM - Collapsible */}
        <SectionCard style={{marginBottom:'1.5rem'}}>
          <CardTitle><span className="card-icon"><FaBookOpen /></span>Course Curriculum</CardTitle>
          <p style={{color:'#4a4a6a', fontSize:'0.85rem', marginBottom:'1rem'}}>Click on each module to expand and view detailed topics.</p>
          {curriculum.map((mod, i) => (
            <CurrCard key={i}>
              <CurrHeader open={openModules[i]} onClick={() => toggleModule(i)}>
                <div className="curr-left"><FaBookOpen /> {mod.title}</div>
                <span className="curr-arrow">{openModules[i] ? <FaChevronUp /> : <FaChevronDown />}</span>
              </CurrHeader>
              <CurrBody open={openModules[i]}>
                <ul>{mod.items.map((item, j) => <li key={j}>{item}</li>)}</ul>
              </CurrBody>
            </CurrCard>
          ))}
        </SectionCard>

        {/* CERTIFICATION & FEES */}
        <CertBlock>
          <CertHead>
            <CertHeadTitle>Inpatient DRG Coding Certification</CertHeadTitle>
            <CertHeadBadge>Hospital Coding</CertHeadBadge>
          </CertHead>
          <CertBody>
            <CertDesc>
              Master the art of Inpatient DRG Coding with our comprehensive training program. 
              Learn ICD-10-CM, ICD-10-PCS, and DRG assignment methodologies to excel in hospital 
              inpatient coding and reimbursement.
            </CertDesc>

            <CertCols>
              <div>
                <CertColTitle><FaAward /> What You'll Learn</CertColTitle>
                <PlainList>
                  {whatYoullLearn.map((item, i) => (
                    <li key={i}><FaCheckCircle />{item}</li>
                  ))}
                </PlainList>
              </div>
              <div>
                <CertColTitle><FaListUl /> Course Inclusions</CertColTitle>
                <PlainList>
                  {inclusions.map((inc, i) => (
                    <li key={i}><FaCheckCircle />{inc}</li>
                  ))}
                </PlainList>
              </div>
            </CertCols>

            <FeeTable>
              <thead>
                <tr>
                  <th>Component</th>
                  <th>Description</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="highlight">
                  <td><strong>Course Fee</strong></td>
                  <td>To be paid to LearnThruUs Academy</td>
                  <td><span className="amt">₹20,000 + GST</span></td>
                </tr>
              </tbody>
            </FeeTable>
          </CertBody>
        </CertBlock>

        {/* CTA */}
        <CTASection>
          <CTAText>
            <div className="cta-title">Ready to Master Inpatient DRG Coding?</div>
            <div className="cta-sub">Join our Inpatient DRG Coding Training and advance your hospital coding career.</div>
          </CTAText>
          <CTAButton to="/contact-us">
            Enroll Now <FaArrowRight />
          </CTAButton>
        </CTASection>

        <Spacer />
      </Container>
    </PageContainer>
  );
};

export default InpatientDRGCoding;